'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useAnalytics } from '@/hooks/useAnalytics';

// Feature data with updated icons
const features = [
  {
    name: 'Communication',
    description:
      'Keep everyone connected with direct messaging, group chats, SMS, email, and push notifications.',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-6 h-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"
        />
      </svg>
    ),
    items: [
      'Community forums',
      'Direct messaging',
      'Group messaging',
      'SMS notifications',
      'Email communications',
      'Push notifications',
    ],
    href: '/features/communication',
    color: 'blue',
    gradient: 'from-futuristic-blue via-athletic-blue to-athletic-indigo',
    glow: 'shadow-blue-glow',
  },
  {
    name: 'Train',
    description:
      'Manage camps, clinics, showcases, daily training programs, player evaluations, and video analysis.',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-6 h-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
        />
      </svg>
    ),
    items: [
      'Camps, clinics, showcases',
      'Daily training programs',
      'Off-season training',
      'Player evaluations',
      'Performance tracking',
      'Film room & video analysis',
    ],
    href: '/features/train',
    color: 'indigo',
    gradient: 'from-athletic-indigo via-athletic-purple to-futuristic-purple',
    glow: 'shadow-purple-glow',
  },
  {
    name: 'Compete',
    description:
      'Organize leagues, teams, and tournaments with powerful scheduling and management tools.',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-6 h-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0"
        />
      </svg>
    ),
    items: [
      'League management',
      'Team organization',
      'Tournament scheduling',
      'Game day management',
      'Stats and standings',
      'Playoff brackets',
    ],
    href: '/features/compete',
    color: 'purple',
    gradient: 'from-athletic-purple via-futuristic-purple to-futuristic-neon',
    glow: 'shadow-neon-glow',
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { 
      type: "spring", 
      stiffness: 100, 
      damping: 15,
      duration: 0.6 
    },
  },
};

const titleVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { 
      type: "spring", 
      stiffness: 100, 
      damping: 15,
      duration: 0.5,
      delay: 0.1
    },
  },
};

const subtitleVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { 
      type: "spring", 
      stiffness: 100, 
      damping: 15,
      duration: 0.5,
      delay: 0.2
    },
  },
};

const checkmarkVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { 
      type: "spring", 
      stiffness: 300, 
      damping: 20 
    }
  },
  hover: { 
    scale: 1.1,
    transition: { 
      type: "spring", 
      stiffness: 400, 
      damping: 10 
    }
  }
};

export default function Features() {
  const { trackFeature } = useAnalytics();
  const [isMounted, setIsMounted] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState<string | null>(null);
  
  // Track feature clicks
  const handleFeatureClick = (featureName: string) => {
    trackFeature('feature_card', `click_${featureName.toLowerCase()}`);
  };
  
  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <section id="features" className="relative py-24 overflow-hidden bg-futuristic-black">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-futuristic-blue/10 via-futuristic-purple/5 to-transparent opacity-70"></div>
        
        {/* Grid pattern removed as requested */}
      </div>
      
      {/* Animated particles */}
      {isMounted && (
        <div className="absolute inset-0 z-0">
          <motion.div
            className="absolute top-1/4 left-1/4 w-1 h-1 rounded-full bg-white opacity-80"
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              repeatType: "loop",
            }}
          />
          <motion.div
            className="absolute top-1/3 left-2/3 w-2 h-2 rounded-full bg-athletic-blue opacity-60"
            animate={{
              y: [0, -150, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              repeatType: "loop",
              delay: 1,
            }}
          />
          <motion.div
            className="absolute top-2/3 left-1/3 w-1.5 h-1.5 rounded-full bg-athletic-purple opacity-70"
            animate={{
              y: [0, -120, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 9,
              repeat: Infinity,
              repeatType: "loop",
              delay: 2,
            }}
          />
        </div>
      )}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-white to-gray-400 bg-clip-text text-transparent"
            variants={titleVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            All the tools you need under one roof
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-300"
            variants={subtitleVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            Sketchplay simplifies sports management with three powerful pillars
          </motion.p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16"
        >
          {features.map((feature) => (
            <motion.div
              key={feature.name}
              variants={itemVariants}
              className="relative group"
              onMouseEnter={() => setHoveredFeature(feature.name)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div 
                className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-700 -z-10`}
              />
              <div 
                className={`absolute inset-0 rounded-2xl ${feature.glow} opacity-0 group-hover:opacity-20 transition-opacity duration-700 -z-10 blur-xl`}
              />
              <div className="bg-futuristic-dark/80 backdrop-blur-sm rounded-2xl group-hover:border-white/10 transition-all duration-500 overflow-hidden h-full flex flex-col">
                {/* Gradient top border */}
                <div className={`h-1 w-full bg-gradient-to-r ${feature.gradient}`} />
                
                <div className="p-8 flex-1 flex flex-col">
                  <div className="flex items-start justify-between mb-6">
                    <motion.div
                      className={`w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br ${feature.gradient} ${feature.glow} p-0.5`}
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <div className="bg-futuristic-black/90 rounded-lg w-full h-full flex items-center justify-center">
                        <div className="text-white">
                          {feature.icon}
                        </div>
                      </div>
                    </motion.div>
                    
                    <motion.div 
                      className="text-xs uppercase tracking-widest text-white/50 font-medium"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                    >
                      {feature.color === 'blue' ? '01' : feature.color === 'indigo' ? '02' : '03'}
                    </motion.div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-gradient group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-400 transition-all duration-300">
                    {feature.name}
                  </h3>
                  
                  <p className="text-gray-300 mb-8 flex-grow">{feature.description}</p>
                  
                  <ul className="space-y-4 mb-8">
                    {feature.items.map((item, index) => (
                      <motion.li 
                        key={item} 
                        className="flex items-start"
                        variants={checkmarkVariants}
                        initial="hidden"
                        whileInView="visible"
                        whileHover="hover"
                        viewport={{ once: true }}
                        custom={index}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className={`flex-shrink-0 h-5 w-5 mr-3 mt-0.5 rounded-full bg-gradient-to-br ${feature.gradient} flex items-center justify-center`}>
                          <svg
                            className="h-3 w-3 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <span className="text-gray-300 group-hover:text-white transition-colors duration-300">{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                  
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <Button
                      variant="neon"
                      href={feature.href}
                      className={`w-full justify-center py-6 text-lg font-medium rounded-full group-hover:border-opacity-30 relative overflow-hidden shadow-neon-glow hover:shadow-[0_0_40px_rgba(0,255,102,0.7)] transition-all duration-300`}
                      trackingName={`feature_${feature.name.toLowerCase()}_button`}
                      trackingLocation="homepage_features"
                      onClick={() => handleFeatureClick(feature.name)}
                    >
                      <span className="relative z-10">Learn More</span>
                      <motion.div 
                        className={`absolute inset-0 -z-0 bg-gradient-to-r ${feature.gradient} opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-full`}
                        initial={false}
                        animate={{ 
                          opacity: hoveredFeature === feature.name ? 0.2 : 0 
                        }}
                        transition={{ duration: 0.3 }}
                      />
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
} 